import React, { useState, useEffect, useRef, useMemo, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from '../components/common/toast';
import { returnService, productService } from '../services/api';
import { Table, Badge, EditIcon, DeleteIcon, FormPopup, DeletePopUp, ReturnDetailsModal } from '../components/common';
import SkeletonTable from '../components/SkeletonTable';

const getStatusVariant = (status) => {
  return 'success'; // Only RECEIVED - from backend
};

// Status is read-only from backend; no status change allowed
function ReturnStatusCell({ status, getStatusVariant }) {
  if (!status) return '—';
  return (
    <Badge variant={getStatusVariant(status)}>
      <span>{status.replace(/_/g, ' ')}</span>
    </Badge>
  );
}

function getReturnBatchStateKey(batch, index = 0) {
  const stableId = batch?.batchId || batch?._id || batch?.id;
  if (stableId != null && String(stableId).trim() !== '') {
    return String(stableId);
  }
  const batchNumber = String(batch?.batchNumber || '').trim();
  const mfg = String(batch?.manufacturingDate || batch?.mfgDate || '').trim();
  const exp = String(batch?.expiryDate || '').trim();
  return `${batchNumber}::${mfg}::${exp}::${index}`;
}

function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  const updateMenuPosition = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 100000,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return;
    }
    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (
        containerRef.current?.contains(e.target) ||
        dropdownRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    const selected = options.find((o) => o.value === value);
    if (!open && selected) {
      setQuery(selected.label);
    }
  }, [value, options, open]);

  const handleInputFocus = () => {
    if (disabled) return;
    setQuery('');
    setOpen(true);
  };

  const handleSelect = (opt) => {
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    setOpen(false);
  };

  const lowerQuery = query.toLowerCase();
  const filtered =
    lowerQuery.trim().length === 0
      ? options
      : options.filter((opt) => {
          const label = String(opt.label ?? '').toLowerCase();
          const val = String(opt.value ?? '').toLowerCase();
          return label.includes(lowerQuery) || val.includes(lowerQuery);
        });

  const selectedLabel = value
    ? options.find((o) => o.value === value)?.label ?? ''
    : '';

  return (
    <div ref={containerRef} className="relative w-full">
      <input
        type="text"
        value={open ? query : selectedLabel || ''}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={handleInputFocus}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full min-w-0 rounded-[10px] border border-[#D0D5DD] bg-white px-3 text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:ring-2 focus:ring-[rgba(24,135,66,0.12)] overflow-visible ${
          disabled ? 'cursor-not-allowed opacity-60' : ''
        }`}
        style={{ height: 44 }}
      />
      {!disabled && (
        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </div>
      )}
      {open &&
        !disabled &&
        menuStyle &&
        createPortal(
          <div
            ref={dropdownRef}
            className="rounded-[10px] border border-[#D0D5DD] bg-white shadow-lg overflow-hidden"
            style={menuStyle}
          >
            <div className="overflow-y-auto overscroll-contain py-1" style={{ maxHeight: 220 }}>
              {filtered.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500">No results</div>
              )}
              {filtered.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  disabled={!!opt.disabled}
                  title={opt.title}
                  onClick={() => handleSelect(opt)}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-[#F6F7FA] ${
                    opt.disabled
                      ? 'cursor-not-allowed text-gray-400'
                      : value === opt.value
                        ? 'bg-[rgba(24,135,66,0.08)] text-[#188742]'
                        : 'text-gray-800'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

const FULLY_RETURNED_PRODUCT_MESSAGE =
  'This product has already been fully returned. No additional return can be created.';
const NO_RETURNABLE_PRODUCTS_MESSAGE =
  'All products in this outward have already been fully returned. No returnable products remain.';
const FULLY_RETURNED_DO_TOOLTIP =
  'All products from this outward have already been returned.';

function getInvoiceReturnableSummary(invoice) {
  const items = Array.isArray(invoice?.items) ? invoice.items : [];
  let returnableItemCount = 0;
  let totalReturnableQty = 0;

  items.forEach((item) => {
    const remaining = getRemainingReturnableQuantity({
      dispatchQuantity: item?.dispatchQuantity || 0,
      batchDetails: item?.batchDetails || [],
    });
    if (remaining > 0) {
      returnableItemCount += 1;
      totalReturnableQty += remaining;
    }
  });

  return {
    returnableItemCount,
    totalReturnableQty,
    isFullyReturned: items.length > 0 && returnableItemCount === 0,
    hasItems: items.length > 0,
  };
}

function formatDoOptionLabel(doNumber, summary) {
  if (summary.isFullyReturned) {
    return `${doNumber} (Fully Returned)`;
  }
  if (summary.returnableItemCount > 1) {
    return `${doNumber} (${summary.returnableItemCount} Items Returnable)`;
  }
  if (summary.totalReturnableQty > 0) {
    return `${doNumber} (${summary.totalReturnableQty} Qty Returnable)`;
  }
  return `${doNumber} (Return Available)`;
}

function getNumericValue(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

function getRemainingReturnableQuantity(item) {
  const dispatchQuantity = Math.max(0, getNumericValue(item?.dispatchQuantity));
  const batchDetails = Array.isArray(item?.batchDetails) ? item.batchDetails : [];

  if (batchDetails.length === 0) {
    return dispatchQuantity;
  }

  const hasAlreadyReturnedData = batchDetails.some((batch) => batch?.alreadyReturned != null);
  const alreadyReturnedQuantity = batchDetails.reduce(
    (sum, batch) => sum + Math.max(0, getNumericValue(batch?.alreadyReturned)),
    0
  );

  if (hasAlreadyReturnedData) {
    return Math.max(0, dispatchQuantity - alreadyReturnedQuantity);
  }

  const availableQuantity = batchDetails.reduce(
    (sum, batch) => sum + Math.max(0, getNumericValue(batch?.availableQuantity ?? batch?.quantity)),
    0
  );
  return Math.max(0, Math.min(dispatchQuantity, availableQuantity));
}

export function ReturnManagement() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const limit = 20;
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [outwardInvoices, setOutwardInvoices] = useState([]);
  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [batchSelections, setBatchSelections] = useState({});
  // OLD
  // const [receiveQty, setReceiveQty] = useState('');
  // NEW: store qty per batch
  const [receiveQtyMap, setReceiveQtyMap] = useState({});
  const [batchApplyError, setBatchApplyError] = useState('');
  // CURRENT - WRONG (derived from batch inputs, not user input)
  // const [totalReceivedQty, setTotalReceivedQty] = useState(0);
  // OLD - CORRECT: user-entered received qty (can be <= dispatched)
  const [totalReceivedQtyMap, setTotalReceivedQtyMap] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [formData, setFormData] = useState({
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    productId: '',
    invoiceQuantity: 0,
    receivedQuantity: 0,
    returnReason: 'DEFECTIVE',
    vehicleNumber: '',
    location: '',
    batches: [],
  });
  const [currentBatch, setCurrentBatch] = useState({
    batchNumber: '',
    manufacturingDate: '',
    expiryDate: '',
    quantity: 0,
  });
  const [deletePopUpOpen, setDeletePopUpOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const {
    data: returnTransactionsResponse,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['returnTransactions', page, limit],
    queryFn: async () => {
      const params = { page, limit };
      return returnService.getReturnTransactions(params);
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });
  const loading = isLoading || isFetching;

  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewError, setViewError] = useState(null);
  const viewRowRef = useRef(null);

  // Normalize search once for smart, case-insensitive filtering (like Product Master)
  const normalizedSearch = useMemo(
    () => (searchTerm ? searchTerm.trim().toLowerCase() : ''),
    [searchTerm]
  );

  // Frontend smart search over common visible fields (do not rely on backend search matching)
  const filteredTransactions = useMemo(() => {
    if (!normalizedSearch) return transactions;
    const term = normalizedSearch;
    return (transactions || []).filter((t) => {
      const txId = String(t.transactionId ?? t._id ?? t.id ?? '').toLowerCase();
      const invoice = String(t.invoiceNumber ?? '').toLowerCase();
      const reason = String(t.returnReason ?? '').toLowerCase();
      const vehicle = String(t.vehicleNumber ?? '').toLowerCase();
      const status = String(t.status ?? '').toLowerCase();
      const location = String(t.location ?? '').toLowerCase();

      const productObj = t.productId && typeof t.productId === 'object' ? t.productId : null;
      const sku = String(productObj?.sku ?? t.productSku ?? t.productCode ?? t.productId ?? '').toLowerCase();
      const nameDesc = String(productObj?.skuName ?? productObj?.description ?? t.productDescription ?? '').toLowerCase();

      return (
        txId.includes(term) ||
        invoice.includes(term) ||
        sku.includes(term) ||
        nameDesc.includes(term) ||
        reason.includes(term) ||
        vehicle.includes(term) ||
        status.includes(term) ||
        location.includes(term)
      );
    });
  }, [transactions, normalizedSearch]);

  const getProductBatchTotal = (productId) =>
    Object.values(batchSelections[String(productId)] || {}).reduce(
      (sum, val) => sum + Number(val || 0),
      0
    );

  const getProductReceivedQtyNumber = (productId) =>
    Number(totalReceivedQtyMap[String(productId)] || 0);

  const invoiceProducts = useMemo(() => {
    if (!selectedInvoiceData) return [];

    const invoice = outwardInvoices.find(
      (inv) => (inv.deliveryOrderNumber || inv.invoiceNumber) === selectedInvoiceData.invoiceNumber
    );

    if (!invoice || !Array.isArray(invoice.items) || invoice.items.length === 0) {
      return [];
    }

    return invoice.items
      .map((item) => {
        let product = item.product;
        const productId = item.productId;

        if (!product || typeof product === 'string') {
          product = products.find((p) => p._id === productId || String(p._id) === String(productId));
        }

        const normalizedItem = {
          productId,
          product,
          dispatchQuantity: item.dispatchQuantity || 0,
          batchDetails: item.batchDetails || [],
        };

        const remainingReturnable = getRemainingReturnableQuantity(normalizedItem);

        return {
          ...normalizedItem,
          remainingReturnable,
          isFullyReturned: !editingTransactionId && remainingReturnable <= 0,
        };
      })
      .filter((item) => item.productId);
  }, [selectedInvoiceData, outwardInvoices, products, editingTransactionId]);

  const selectedInvoiceProduct =
    selectedProducts.length > 0
      ? (
          invoiceProducts.find(
            (item) => String(item.productId) === String(selectedProducts[0]?.productId || '')
          ) || null
        )
      : null;

  const selectedProductRemainingReturnable = selectedInvoiceProduct
    ? getNumericValue(selectedInvoiceProduct.remainingReturnable)
    : null;

  const selectedProductFullyReturned =
    !editingTransactionId &&
    !!selectedInvoiceProduct &&
    Number(selectedProductRemainingReturnable || 0) <= 0;

  const allInvoiceProductsFullyReturned =
    !editingTransactionId &&
    !!selectedInvoiceData &&
    invoiceProducts.length > 0 &&
    invoiceProducts.every((item) => item.isFullyReturned);

  const batchAllocationValidationMessage = useMemo(() => {
    if (selectedProducts.length === 0) return '';
    if (batchApplyError) return batchApplyError;

    const messages = [];
    selectedProducts.forEach((product) => {
      const pid = String(product.productId);
      const invoiceProduct = invoiceProducts.find((item) => String(item.productId) === pid);
      const productLabel = invoiceProduct?.product?.sku || pid;

      if (!editingTransactionId && invoiceProduct?.isFullyReturned) {
        messages.push(`${productLabel}: ${FULLY_RETURNED_PRODUCT_MESSAGE}`);
        return;
      }

      const receivedQty = Number(totalReceivedQtyMap[pid] || 0);
      const batchTotal = Object.values(batchSelections[pid] || {}).reduce(
        (sum, val) => sum + Number(val || 0),
        0
      );

      if (receivedQty <= 0) return;
      if (batchTotal > receivedQty) {
        messages.push(
          `${productLabel}: Total allocated batch quantity (${batchTotal}) cannot exceed the Return Quantity (${receivedQty}). Please remove or adjust the allocation.`
        );
      } else if (batchTotal < receivedQty) {
        messages.push(
          `${productLabel}: Total allocated batch quantity (${batchTotal}) must equal the Return Quantity (${receivedQty}) before creating the return.`
        );
      }
    });

    return messages.join(' ');
  }, [
    selectedProducts,
    invoiceProducts,
    editingTransactionId,
    totalReceivedQtyMap,
    batchSelections,
    batchApplyError,
  ]);

  const isReturnQtyInvalid = useMemo(() => {
    if (selectedProducts.length === 0) return true;

    return selectedProducts.some((product) => {
      const pid = String(product.productId);
      const invoiceProduct = invoiceProducts.find((item) => String(item.productId) === pid);

      if (!editingTransactionId && invoiceProduct?.isFullyReturned) return true;

      const receivedQty = Number(totalReceivedQtyMap[pid] || 0);
      const batchTotal = Object.values(batchSelections[pid] || {}).reduce(
        (sum, val) => sum + Number(val || 0),
        0
      );
      const hasValidBatchQty = Object.values(batchSelections[pid] || {}).some(
        (val) => Number(val || 0) > 0
      );

      if (receivedQty <= 0) return true;
      if (batchTotal <= 0) return true;
      if (!hasValidBatchQty) return true;
      if (batchTotal !== receivedQty) return true;
      if (!Array.isArray(product.batchDetails) || product.batchDetails.length === 0) return true;
      return false;
    });
  }, [
    selectedProducts,
    invoiceProducts,
    editingTransactionId,
    totalReceivedQtyMap,
    batchSelections,
  ]);

  const doNumberSelectOptions = useMemo(() => {
    return (outwardInvoices || []).map((inv) => {
      const doNumber = inv.deliveryOrderNumber || inv.invoiceNumber;
      const summary = getInvoiceReturnableSummary(inv);
      const isEditingCurrentDo =
        !!editingTransactionId && formData.invoiceNumber === doNumber;
      return {
        label: formatDoOptionLabel(doNumber, summary),
        value: doNumber,
        disabled: summary.isFullyReturned && !isEditingCurrentDo,
        title: summary.isFullyReturned ? FULLY_RETURNED_DO_TOOLTIP : undefined,
      };
    });
  }, [outwardInvoices, editingTransactionId, formData.invoiceNumber]);

  const returnColumns = [
    { key: 'transactionId', label: 'Transaction ID' },
    { key: 'invoiceNumber', label: 'DO Number' },
    {
      key: 'productId',
      label: 'Product',
      render: (val) => (val?.sku ?? '—'),
    },
    { key: 'receivedQuantity', label: 'Received Qty', align: 'right' },
    { key: 'returnReason', label: 'Return Reason' },
    {
      key: 'vehicleNumber',
      label: 'Vehicle',
      render: (v) => v || '—',
    },
    {
      key: 'status',
      label: 'Status',
      render: (val) => (
        <ReturnStatusCell status={val} getStatusVariant={getStatusVariant} />
      ),
    },
  ];

  useEffect(() => {
    fetchProducts();
    fetchOutwardInvoices();
  }, []);

  useEffect(() => {
    setTransactions(returnTransactionsResponse?.data || []);
  }, [returnTransactionsResponse]);

  // CURRENT - WRONG: auto syncing received qty from batch inputs
  // useEffect(() => {
  //   const total = Object.values(receiveQtyMap).reduce(
  //     (sum, val) => sum + Number(val || 0),
  //     0
  //   );
  //   setTotalReceivedQty(total);
  // }, [receiveQtyMap]);

  // Debounce search
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     fetchTransactions();
  //   }, 300);
  //   return () => clearTimeout(timer);
  // }, [searchTerm]);
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);
  // Also watch for outwardInvoices loading - if invoiceNumber is already set, update vehicle number
  useEffect(() => {
    if (formData.invoiceNumber && outwardInvoices.length > 0 && showForm) {
      const invoice = outwardInvoices.find(
        (inv) => (inv.deliveryOrderNumber || inv.invoiceNumber) === formData.invoiceNumber
      );
      
      if (invoice && invoice.vehicleNumber) {
        // Update vehicle number if it's different or not set
        setFormData((prev) => {
          if (prev.vehicleNumber !== invoice.vehicleNumber) {
            return {
              ...prev,
              vehicleNumber: invoice.vehicleNumber,
            };
          }
          return prev;
        });
      }
    }
  }, [outwardInvoices.length, formData.invoiceNumber, showForm]);

  // Watch for invoice number changes and auto-populate invoice date and vehicle number
  useEffect(() => {
    if (formData.invoiceNumber && outwardInvoices.length > 0) {
      const invoice = outwardInvoices.find(
        (inv) => (inv.deliveryOrderNumber || inv.invoiceNumber) === formData.invoiceNumber
      );

      if (invoice) {
        const summary = getInvoiceReturnableSummary(invoice);
        const isEditingCurrentDo =
          !!editingTransactionId && formData.invoiceNumber === (invoice.deliveryOrderNumber || invoice.invoiceNumber);
        // ❌ OLD: fully returned DOs could be selected; popup then showed NO_RETURNABLE_PRODUCTS_MESSAGE
        if (summary.isFullyReturned && !isEditingCurrentDo) {
          setSelectedInvoiceData(null);
          setSelectedProducts([]);
          setBatchSelections({});
          setReceiveQtyMap({});
          setBatchApplyError('');
          setTotalReceivedQtyMap({});
          setFormData((prev) => (
            prev.invoiceNumber === formData.invoiceNumber
              ? { ...prev, invoiceNumber: '', vehicleNumber: '' }
              : prev
          ));
          return;
        }

        // Set selected invoice data
        setSelectedInvoiceData(invoice);
        // Reset selected products when invoice changes
        setSelectedProducts([]);
        setBatchSelections({});
        // NEW: keep batch inputs empty initially (no auto sync)
        setReceiveQtyMap({});
        setBatchApplyError('');
        setTotalReceivedQtyMap({});
        
        // Prepare update data
        const updateData = {};
        
        // Parse and set invoice date
        if (invoice.invoiceDate) {
          try {
            let dateObj;
            const invoiceDateValue = invoice.invoiceDate;
            
            // Handle different date formats
            if (typeof invoiceDateValue === 'string') {
              dateObj = new Date(invoiceDateValue);
            } else if (invoiceDateValue instanceof Date) {
              dateObj = invoiceDateValue;
            } else if (invoiceDateValue && typeof invoiceDateValue === 'object' && invoiceDateValue.$date) {
              // MongoDB date format
              dateObj = new Date(invoiceDateValue.$date);
            } else {
              dateObj = new Date(invoiceDateValue);
            }
            
            if (!isNaN(dateObj.getTime())) {
              const formattedDate = dateObj.toISOString().split('T')[0];
              updateData.invoiceDate = formattedDate;
            } else {
              console.warn('Invalid date format:', invoiceDateValue);
            }
          } catch (error) {
            console.error('Error parsing invoice date:', error, invoice.invoiceDate);
          }
        }
        
        // Always set vehicle number (don't depend on date parsing)
        updateData.vehicleNumber = invoice.vehicleNumber || '';
        
        // Update form data - always update vehicle number when invoice changes
        setFormData((prev) => {
          const newData = { ...prev };
          let hasChanges = false;
          
          // Update invoice date if different
          if (updateData.invoiceDate && prev.invoiceDate !== updateData.invoiceDate) {
            newData.invoiceDate = updateData.invoiceDate;
            hasChanges = true;
          }
          
          // Always update vehicle number when invoice changes (even if same value, to ensure it's set)
          if (prev.vehicleNumber !== updateData.vehicleNumber) {
            newData.vehicleNumber = updateData.vehicleNumber;
            hasChanges = true;
          }
          
          return hasChanges ? newData : prev;
        });
      }
    }
  }, [formData.invoiceNumber, outwardInvoices, editingTransactionId]);

  const fetchProducts = async () => {
    try {
      const result = await productService.getProducts();
      setProducts(result.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchTransactions = async () => {
    await queryClient.invalidateQueries({ queryKey: ['returnTransactions'] });
  };

  const fetchOutwardInvoices = async () => {
    try {
      const result = await returnService.getOutwardInvoicesForReturn();
      setOutwardInvoices(result.data || []);
    } catch (error) {
      console.error('Error fetching outward invoices:', error);
      toast.error('Failed to load outward invoices');
    }
  };

  // Get unique invoice numbers from outward invoices (legacy shape for vehicle/date lookup)
  const getUniqueInvoices = () => {
    return outwardInvoices.map((inv) => ({
      invoiceNumber: inv.deliveryOrderNumber || inv.invoiceNumber,
      invoiceDate: inv.invoiceDate,
      vehicleNumber: inv.vehicleNumber,
      outwardTransactionId: inv.outwardTransactionId,
    }));
  };

  const resetForm = () => {
    setFormData({
      invoiceNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      productId: '',
      invoiceQuantity: 0,
      receivedQuantity: 0,
      returnReason: 'DEFECTIVE',
      vehicleNumber: '',
      location: '',
      batches: [],
    });
    setSelectedProducts([]);
    setBatchSelections({});
    // OLD auto-fill logic
    // setReceiveQtyMap({ batch1: product.dispatchedQty })
    // NEW: keep batch inputs empty initially
    setReceiveQtyMap({});
    setBatchApplyError('');
    setTotalReceivedQtyMap({});
    setSelectedInvoiceData(null);
    setFormErrors({});
    setTouched({});
    setEditingTransactionId(null);
    setCurrentBatch({
      batchNumber: '',
      manufacturingDate: '',
      expiryDate: '',
      quantity: 0,
    });
  };

  // Wrapper for setFormData that includes validation
  const handleSetFormData = (updater) => {
    setFormData((prevFormData) => {
      const newFormData = typeof updater === 'function' ? updater(prevFormData) : updater;
      
      // Validate received quantity whenever it changes
      if (newFormData.receivedQuantity !== prevFormData.receivedQuantity) {
        setTouched((prev) => ({ ...prev, receivedQuantity: true }));
        
        const receivedQty = parseFloat(newFormData.receivedQuantity) || 0;
        const invoiceQty = newFormData.invoiceQuantity || 0;
        
        if (receivedQty < 0) {
          setFormErrors((prev) => ({
            ...prev,
            receivedQuantity: 'Received quantity cannot be negative',
          }));
        } else if (receivedQty > invoiceQty && invoiceQty > 0) {
          setFormErrors((prev) => ({
            ...prev,
            receivedQuantity: `Received quantity cannot exceed invoice quantity (${invoiceQty})`,
          }));
        } else {
          // Clear error if valid
          setFormErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.receivedQuantity;
            return newErrors;
          });
        }
        
        // OLD batch UI removed: batch validation now happens using `batchSelections` on submit.
        // (Keep legacy `batches` state intact for backend edit payloads / backwards-compat.)
      }
      
      return newFormData;
    });
  };

  const handleProductToggle = (productId, dispatchQuantity, batchDetails = [], remainingReturnable = null) => {
    if (!editingTransactionId && Number(remainingReturnable) <= 0) {
      return;
    }
    setSelectedProducts((prev) => {
      const productIdStr = String(productId);
      const isSelected = prev.some((p) => String(p.productId) === productIdStr);

      if (editingTransactionId) {
        if (isSelected) {
          return prev;
        }

        const productItem = invoiceProducts.find(
          (item) => String(item.productId) === productIdStr
        );
        const batches = productItem?.batchDetails || batchDetails || [];

        setFormData((prevForm) => ({
          ...prevForm,
          productId,
          invoiceQuantity: dispatchQuantity,
          receivedQuantity: 0,
        }));
        setTotalReceivedQtyMap({});
        setReceiveQtyMap({});
        setBatchApplyError('');
        setBatchSelections({});

        return [{ productId, dispatchQuantity, batchDetails: batches, receivedQuantity: 0 }];
      }

      if (isSelected) {
        const nextSelected = prev.filter((p) => String(p.productId) !== productIdStr);

        setTotalReceivedQtyMap((prevQtyMap) => {
          const nextQtyMap = { ...prevQtyMap };
          delete nextQtyMap[productIdStr];
          return nextQtyMap;
        });
        setBatchApplyError('');
        setReceiveQtyMap((prevReceiveMap) => {
          const nextReceiveMap = { ...prevReceiveMap };
          Object.keys(nextReceiveMap).forEach((key) => {
            if (key.startsWith(`${productIdStr}:`)) {
              delete nextReceiveMap[key];
            }
          });
          return nextReceiveMap;
        });
        setBatchSelections((prevSel) => {
          const next = { ...prevSel };
          delete next[productIdStr];
          return next;
        });

        if (nextSelected.length === 0) {
          setFormData((prevForm) => ({
            ...prevForm,
            productId: '',
            invoiceQuantity: 0,
            receivedQuantity: 0,
          }));
        } else if (String(formData.productId) === productIdStr) {
          const nextProduct = nextSelected[0];
          setFormData((prevForm) => ({
            ...prevForm,
            productId: nextProduct.productId,
            invoiceQuantity: nextProduct.dispatchQuantity,
            receivedQuantity: 0,
          }));
        }

        return nextSelected;
      }

      const productItem = invoiceProducts.find(
        (item) => String(item.productId) === productIdStr
      );
      const batches = productItem?.batchDetails || batchDetails || [];
      const newInvoiceQty = dispatchQuantity;

      setFormData((prevForm) => {
        const updated = {
          ...prevForm,
          productId,
          invoiceQuantity: newInvoiceQty,
          receivedQuantity: 0,
        };

        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.receivedQuantity;
          delete newErrors.batches;
          return newErrors;
        });

        return updated;
      });
      setBatchApplyError('');
      setBatchSelections((prevSel) => ({
        ...prevSel,
        [productIdStr]: prevSel[productIdStr] || {},
      }));

      return [
        ...prev,
        { productId, dispatchQuantity, batchDetails: batches, receivedQuantity: 0 },
      ];
    });
  };

  const handleBatchChange = (e) => {
    const { name, value } = e.target;
    setCurrentBatch({
      ...currentBatch,
      [name]: name === 'quantity' ? parseFloat(value) : value,
    });
  };

  const addBatch = () => {
    if (currentBatch.batchNumber && currentBatch.quantity > 0) {
      setFormData({
        ...formData,
        batches: [...formData.batches, { ...currentBatch }],
      });
      setCurrentBatch({
        batchNumber: '',
        manufacturingDate: '',
        expiryDate: '',
        quantity: 0,
      });
    }
  };

  const removeBatch = (index) => {
    setFormData({
      ...formData,
      batches: formData.batches.filter((_, i) => i !== index),
    });
  };

  const handleReturnQtyChange = (productId, value) => {
    const productKey = String(productId);
    setTotalReceivedQtyMap((prev) => ({
      ...prev,
      [productKey]: value,
    }));
    setBatchApplyError('');
  };

  const handleBatchQtyInputChange = (inputKey, value) => {
    setReceiveQtyMap((prev) => ({
      ...prev,
      [inputKey]: value,
    }));
    setBatchApplyError('');
  };

  const applyBatchAllocation = (productId, batchKey, batchNumber, inputKey, maxQty) => {
    const requestedQty = Number(receiveQtyMap[inputKey] || 0);
    const allowedQty = Number(maxQty || 0);
    const productKey = String(productId);
    const productReceivedQty = getProductReceivedQtyNumber(productKey);
    if (productReceivedQty <= 0) {
      setBatchApplyError('Please enter Return Quantity before applying batch allocation.');
      return;
    }
    if (requestedQty <= 0) {
      setBatchApplyError('Please enter a batch quantity greater than 0 before applying.');
      return;
    }
    if (requestedQty > allowedQty) {
      setBatchApplyError(`Batch allocation for ${batchNumber} cannot exceed the available quantity (${allowedQty}).`);
      return;
    }

    const currentSelections = batchSelections[productKey] || {};
    const currentTotal = Object.values(currentSelections).reduce(
      (sum, val) => sum + Number(val || 0),
      0
    );
    const proposedTotal = currentTotal + requestedQty;
    if (proposedTotal > productReceivedQty) {
      setBatchApplyError(
        `Total allocated batch quantity (${proposedTotal}) cannot exceed the Return Quantity (${productReceivedQty}). Please remove or adjust the allocation.`
      );
      return;
    }

    setBatchSelections((prev) => ({
      ...prev,
      [productKey]: {
        ...(prev[productKey] || {}),
        [batchKey]: requestedQty,
      },
    }));
    setBatchApplyError('');
  };

  const removeBatchAllocation = (productId, batchKey, inputKey) => {
    const productKey = String(productId);
    setBatchSelections((prev) => {
      const nextProductSelections = { ...(prev[productKey] || {}) };
      delete nextProductSelections[batchKey];
      const next = { ...prev };
      if (Object.keys(nextProductSelections).length > 0) {
        next[productKey] = nextProductSelections;
      } else {
        delete next[productKey];
      }
      return next;
    });
    setReceiveQtyMap((prev) => ({
      ...prev,
      [inputKey]: '',
    }));
    setBatchApplyError('');
  };

  const buildBatchesPayloadForProduct = (product) => {
    const productIdStr = String(product.productId);
    return (product.batchDetails || [])
      .map((b, idx) => {
        const batchNumber = b.batchNumber;
        const batchKey = getReturnBatchStateKey(b, idx);
        const qty = Number(batchSelections[productIdStr]?.[batchKey]) || 0;
        return {
          batchNumber: batchNumber || '',
          manufacturingDate: b.manufacturingDate || b.mfgDate || b.mfg_date || undefined,
          expiryDate: b.expiryDate || b.exp_date || undefined,
          quantity: qty,
        };
      })
      .filter((b) => (Number(b.quantity) || 0) > 0);
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (selectedProducts.length === 0) {
      toast.error('Please select a product');
      return;
    }

    const invoice = outwardInvoices.find(
      (inv) => (inv.deliveryOrderNumber || inv.invoiceNumber) === formData.invoiceNumber
    );

    if (allInvoiceProductsFullyReturned) {
      toast.error(NO_RETURNABLE_PRODUCTS_MESSAGE);
      return;
    }

    if (!invoice || !invoice.outwardTransactionId) {
      toast.error('Invalid invoice selection. Please select a valid invoice.');
      return;
    }

    for (const product of selectedProducts) {
      const invoiceProduct = invoiceProducts.find(
        (item) => String(item.productId) === String(product.productId)
      );
      const remainingReturnable = invoiceProduct
        ? getNumericValue(invoiceProduct.remainingReturnable)
        : Number(product.dispatchQuantity || 0);

      if (!editingTransactionId && remainingReturnable <= 0) {
        toast.error(FULLY_RETURNED_PRODUCT_MESSAGE);
        return;
      }

      const productIdStr = String(product.productId);
      const receivedQty = getProductReceivedQtyNumber(productIdStr);
      const batchTotal = getProductBatchTotal(productIdStr);
      const invoiceQty = product?.dispatchQuantity || formData.invoiceQuantity || 0;

      if (receivedQty <= 0) {
        toast.error('Please enter received qty');
        return;
      }

      if (
        batchTotal <= 0 ||
        !Array.isArray(product?.batchDetails) ||
        product.batchDetails.length === 0
      ) {
        toast.error('Please enter batch qty');
        return;
      }

      if (batchTotal !== receivedQty) {
        toast.error(batchAllocationValidationMessage || 'Total batch quantity must equal received quantity');
        return;
      }

      if (receivedQty < 0) {
        toast.error('Received quantity cannot be negative');
        return;
      }

      if (receivedQty > invoiceQty) {
        toast.error(`Received quantity (${receivedQty}) cannot exceed invoice quantity (${invoiceQty})`);
        return;
      }

      const batchesPayload = buildBatchesPayloadForProduct(product);
      if (!batchesPayload.length || !batchesPayload.some((b) => Number(b.quantity || 0) > 0)) {
        toast.error('Please enter batch qty');
        return;
      }
    }

    const sharedPayload = {
      deliveryOrderNumber: formData.invoiceNumber,
      invoiceDate: formData.invoiceDate,
      returnReason: formData.returnReason,
      vehicleNumber: formData.vehicleNumber || '',
      location: formData.location?.trim() || undefined,
      outwardTransactionId: invoice.outwardTransactionId,
    };

    try {
      let response;
      if (editingTransactionId) {
        const firstProduct = selectedProducts[0];
        const receivedQty = getProductReceivedQtyNumber(firstProduct.productId);
        response = await returnService.updateReturnTransaction(editingTransactionId, {
          ...sharedPayload,
          productId: firstProduct.productId,
          invoiceQuantity: firstProduct.dispatchQuantity,
          receivedQuantity: receivedQty,
          batches: buildBatchesPayloadForProduct(firstProduct),
        });
        if (!response?.success) {
          throw new Error(response?.error || response?.message || 'Failed to update return transaction');
        }
        toast.success(response.message || 'Return transaction updated successfully');
      } else if (selectedProducts.length === 1) {
        const firstProduct = selectedProducts[0];
        const receivedQty = getProductReceivedQtyNumber(firstProduct.productId);
        response = await returnService.createReturnTransaction({
          ...sharedPayload,
          productId: firstProduct.productId,
          invoiceQuantity: firstProduct.dispatchQuantity,
          receivedQuantity: receivedQty,
          batches: buildBatchesPayloadForProduct(firstProduct),
        });
        if (!response?.success) {
          throw new Error(response?.error || response?.message || 'Failed to create return transaction');
        }
        toast.success(response.message || 'Return transaction created successfully');
      } else {
        response = await returnService.createReturnTransaction({
          ...sharedPayload,
          products: selectedProducts.map((product) => ({
            productId: product.productId,
            invoiceQuantity: product.dispatchQuantity,
            receivedQuantity: getProductReceivedQtyNumber(product.productId),
            batches: buildBatchesPayloadForProduct(product),
          })),
        });
        if (!response?.success) {
          throw new Error(response?.error || response?.message || 'Failed to create return transactions');
        }
        toast.success(
          response.message ||
            `${response.count || selectedProducts.length} return transactions created successfully`
        );
      }

      resetForm();
      setShowForm(false);
      await fetchTransactions();
      await fetchOutwardInvoices().catch((refreshError) => {
        console.error('Error refreshing outward invoices:', refreshError);
      });
      window.dispatchEvent(new CustomEvent('putaway-refresh'));
    } catch (error) {
      console.error('Error saving return transaction:', error);
      toast.error(error);
    }
  };

  const handleEdit = async (row) => {
    try {
      // Fetch full transaction details including batches
      const transactionDetails = await returnService.getReturnTransactionById(row._id || row.id);
      const transaction = transactionDetails.data || transactionDetails;
      
      // Find the invoice from outward invoices to get product details
      const invoice = outwardInvoices.find(
        (inv) => (inv.deliveryOrderNumber || inv.invoiceNumber) === transaction.invoiceNumber
      );
      
      // Find product in invoice items
      let productItem = null;
      if (invoice && invoice.items) {
        productItem = invoice.items.find(
          (item) => String(item.productId) === String(transaction.productId)
        );
      }

      const txBatches = Array.isArray(transaction.batches) ? transaction.batches : [];
      const usedTxBatchIndexes = new Set();
      const txBatchSelections = productItem
        ? (productItem.batchDetails || []).reduce((acc, batch, index) => {
            const matchIndex = txBatches.findIndex((txBatch, txIdx) => {
              if (usedTxBatchIndexes.has(txIdx)) return false;
              const sameNumber =
                String(txBatch?.batchNumber || '').trim() === String(batch?.batchNumber || '').trim();
              const txMfg = String(txBatch?.manufacturingDate || txBatch?.mfgDate || '').trim();
              const batchMfg = String(batch?.manufacturingDate || batch?.mfgDate || '').trim();
              const txExp = String(txBatch?.expiryDate || '').trim();
              const batchExp = String(batch?.expiryDate || '').trim();
              const sameMfg = !txMfg || !batchMfg || txMfg === batchMfg;
              const sameExp = !txExp || !batchExp || txExp === batchExp;
              return sameNumber && sameMfg && sameExp;
            });
            if (matchIndex === -1) return acc;
            usedTxBatchIndexes.add(matchIndex);
            const batchKey = getReturnBatchStateKey(batch, index);
            acc[batchKey] = Number(txBatches[matchIndex]?.quantity) || 0;
            return acc;
          }, {})
        : txBatches.reduce((acc, batch, index) => {
            const batchKey = getReturnBatchStateKey(batch, index);
            acc[batchKey] = Number(batch?.quantity) || 0;
            return acc;
          }, {});
      
      // Set selected product
      if (productItem) {
        setSelectedProducts([{
          productId: transaction.productId,
          dispatchQuantity: transaction.invoiceQuantity,
          batchDetails: productItem.batchDetails || [],
          receivedQuantity: transaction.receivedQuantity || 0,
        }]);
      }

      setBatchSelections((prev) => ({
        ...prev,
        [String(transaction.productId)]: txBatchSelections,
      }));
      setReceiveQtyMap(
        Object.entries(txBatchSelections).reduce((acc, [batchKey, qty]) => {
          acc[`${String(transaction.productId)}:${batchKey}`] = String(qty);
          return acc;
        }, {})
      );
      setBatchApplyError('');
      setTotalReceivedQtyMap({
        [String(transaction.productId)]: String(transaction.receivedQuantity || ''),
      });
      
      // Populate form with existing transaction data
      setFormData({
        invoiceNumber: transaction.invoiceNumber || '',
        invoiceDate: transaction.invoiceDate 
          ? new Date(transaction.invoiceDate).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
        productId: transaction.productId?._id || transaction.productId || '',
        invoiceQuantity: transaction.invoiceQuantity || 0,
        receivedQuantity: transaction.receivedQuantity || 0,
        returnReason: transaction.returnReason || 'DEFECTIVE',
        vehicleNumber: transaction.vehicleNumber || '',
        location: transaction.location || '',
        batches: transaction.batches || [],
      });
      
      // Set selected invoice data
      if (invoice) {
        setSelectedInvoiceData(invoice);
      }
      
      setEditingTransactionId(transaction._id || row._id || row.id);
      setShowForm(true);
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      toast.error('Failed to load transaction details');
    }
  };

  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeletePopUpOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!rowToDelete) return;
    const id = rowToDelete._id || rowToDelete.id;
    try {
      setDeleteLoading(true);
      await returnService.deleteReturnTransaction(id);
      setDeletePopUpOpen(false);
      setRowToDelete(null);
      await fetchTransactions();
      await fetchOutwardInvoices().catch((refreshError) => {
        console.error('Error refreshing outward invoices:', refreshError);
      });
      toast.success('Return transaction deleted successfully');
    } catch (err) {
      console.error('Error deleting return transaction:', err);
      toast.error(err?.message || 'Failed to delete return transaction');
    } finally {
      setDeleteLoading(false);
    }
  };

  const returnActions = [
    {
      label: 'Edit',
      icon: EditIcon,
      onClick: handleEdit,
    },
    {
      label: 'Delete',
      icon: DeleteIcon,
      onClick: handleDelete,
    },
  ];

  const openView = async (row) => {
    viewRowRef.current = row;
    setViewOpen(true);
    setViewLoading(true);
    setViewError(null);
    try {
      const res = await returnService.getReturnTransactionById(row?._id || row?.id);
      const transaction = res?.data || res;
      setViewData(transaction);
    } catch (err) {
      console.error('Error fetching return details:', err);
      setViewError(err?.message || 'Failed to load transaction details');
      setViewData(null);
    } finally {
      setViewLoading(false);
    }
  };

  const getReturnFields = () => [
    {
      name: 'invoiceNumber',
      label: 'DO Number',
      type: 'custom',
      required: true,
      // ❌ OLD: plain DO label without return status; fully returned DOs were selectable
      // options: getUniqueInvoices().map((inv) => ({
      //   label: inv.deliveryOrderNumber || inv.invoiceNumber,
      //   value: inv.deliveryOrderNumber || inv.invoiceNumber,
      // })),
      render: () => (
        <div>
          <label className="form-popup-label mb-1.5 block">
            DO Number<span className="ml-1 text-red-500">*</span>
          </label>
          <SearchableSelect
            value={formData.invoiceNumber}
            onChange={(nextValue) => {
              setFormData((prev) => ({ ...prev, invoiceNumber: nextValue }));
            }}
            options={doNumberSelectOptions}
            placeholder="Enter no"
            disabled={loading}
          />
          {touched?.invoiceNumber && formErrors?.invoiceNumber ? (
            <p className="mt-2 text-sm text-red-500">{formErrors.invoiceNumber}</p>
          ) : null}
        </div>
      ),
      grid: '1',
    },
    {
      name: 'invoiceDate',
      label: 'Invoice Date',
      type: 'date',
      required: true,
      grid: '1',
      disabled: true,
    },
    {
      name: 'invoiceQuantity',
      label: 'Invoice Quantity',
      type: 'number',
      required: true,
      min: 1,
      grid: '1',
      disabled: true,
    },
    {
      name: 'returnReason',
      label: 'Return Reason',
      type: 'select',
      required: true,
      options: [
        { label: 'Defective', value: 'DEFECTIVE' },
        { label: 'Expired', value: 'EXPIRED' },
        { label: 'Quality Issue', value: 'QUALITY_ISSUE' },
        { label: 'Customer Return', value: 'CUSTOMER_RETURN' },
        { label: 'Other', value: 'OTHER' },
      ],
      grid: '1',
    },
  ];
  const handleExportReturnGRN = () => {
   // ✅ UI only for now (later backend connect)
   toast.info("GRN export will be added soon.");
 };
  return (
    <div
      className="page-container font-nunito"
      style={{ fontFamily: "'Nunito', sans-serif" }}
    >
      <div className="mb-6 w-full">
        <div
          className="rounded-[28px] border border-[#EEF1F5] bg-[#FFFFFF] px-6 py-4 shadow-sm"
          style={{ boxShadow: "0px 0px 4px 0px #959DA526" }}
        >
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      
      {/* Left */}
      <div className="min-w-0">
        <h1
          style={{
            fontFamily: "Nunito",
            fontSize: 18,
            fontWeight: 600,
            color: "#161616",
            margin: 0,
          }}
          className="whitespace-nowrap"
        >
          Return Management
        </h1>
      </div>

      {/* Right */}
      <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center md:w-auto">
        
        {/* ✅ EXACT same search as Company */}
        <div className="w-full sm:w-[320px]">
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-4 w-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </span>

            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by ID"
              className="h-11 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 text-sm text-gray-700 placeholder:text-gray-400 shadow-sm focus:border-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-100"
            />
        </div>
        </div>

        {/* ✅ EXACT same button style */}
        <button
  type="button"
  onClick={() => {
    resetForm();
    fetchOutwardInvoices();
    setShowForm(true);
  }}
  className="h-11 shrink-0 rounded-xl bg-[#188742] hover:bg-[#146c36] px-6 text-sm font-semibold text-white shadow-sm transition active:scale-[0.98]"
>
  Create Return Transaction
</button>
      </div>
    </div>
  </div>
</div>



      <DeletePopUp
        open={deletePopUpOpen}
        onClose={() => { setDeletePopUpOpen(false); setRowToDelete(null); }}
        onConfirm={handleDeleteConfirm}
        entityLabel="return transaction"
      />
      <FormPopup
        open={showForm}
        title={editingTransactionId ? "Edit Return Transaction" : "Create New Return"}
        submitText={editingTransactionId ? "Update" : "Create Return"}
        onClose={() => {
          resetForm();
          setShowForm(false);
        }}
        onSubmit={handleSubmit}
        submitDisabled={
          loading ||
          allInvoiceProductsFullyReturned ||
          (selectedProducts.length > 0 && isReturnQtyInvalid)
        }
        values={formData}
        setValues={handleSetFormData}
        fields={getReturnFields()}
        errors={formErrors}
        touched={touched}
        width="xl"
        bodyGridColumns={4}
      >
        {selectedInvoiceData && (
          <div className="mt-6 rounded-3xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Products</h3>
            {allInvoiceProductsFullyReturned && (
              <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {NO_RETURNABLE_PRODUCTS_MESSAGE}
              </div>
            )}
            {/* Product selection list (selected product shows its allocation panel directly underneath) */}
            <div className="space-y-2">
              {invoiceProducts.map((item, index) => {
                const pid = String(item.productId);
                const isSelected = selectedProducts.some((p) => String(p.productId) === pid);
                const product = item.product;
                const productKey = item.productId || `product-${index}`;
                const remainingReturnable = getNumericValue(item.remainingReturnable);
                const isFullyReturned = item.isFullyReturned;
                const dispatchedQty = Number(item.dispatchQuantity || 0);
                const batches = Array.isArray(item.batchDetails) ? item.batchDetails : [];

                return (
                  <div key={productKey}>
                    <label
                      className={`flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 ${
                        isFullyReturned ? 'cursor-not-allowed bg-gray-50 opacity-70' : 'cursor-pointer hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isFullyReturned}
                        onChange={() => handleProductToggle(item.productId, item.dispatchQuantity, item.batchDetails, remainingReturnable)}
                        className="w-4 h-4 text-emerald-600 border-gray-300 focus:ring-emerald-500"
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {product?.sku || item.productId || 'N/A'} - {product?.skuName || 'Product not found'}
                        </div>
                        <div className="text-xs text-gray-500">
                          Dispatched: <span className="font-medium">{item.dispatchQuantity}</span>
                          <span className="mx-2">|</span>
                          Remaining Returnable: <span className="font-medium">{remainingReturnable}</span>
                        </div>
                        {isFullyReturned && (
                          <div className="mt-1 text-xs text-red-600">
                            {FULLY_RETURNED_PRODUCT_MESSAGE}
                          </div>
                        )}
                      </div>
                    </label>

                    {isSelected && (
                      <div className="mt-2 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                          <div className="text-sm text-gray-700">
                            Received:{' '}
                            <input
                              type="number"
                              min="0"
                              max={dispatchedQty}
                              className="ml-2 w-24 border rounded px-2 py-1 text-sm"
                              value={totalReceivedQtyMap[pid] ?? ''}
                              disabled={isFullyReturned}
                              onChange={(e) => handleReturnQtyChange(pid, e.target.value)}
                            />
                            <span className="ml-2 text-gray-500">/ {dispatchedQty}</span>
                          </div>
                        </div>

                        <div className="mt-3">
                          <div className="grid grid-cols-6 gap-3 border-b border-gray-200 pb-2 text-xs font-semibold text-gray-700">
                            <span>Batch</span>
                            <span>Mfg</span>
                            <span>Exp</span>
                            <span className="text-right">Available</span>
                            <span className="text-right">Alloc Qty</span>
                            <span className="text-right">Action</span>
                          </div>

                          <div className="max-h-[280px] overflow-auto">
                            {batches.length > 0 ? (
                              batches.map((b, idx) => {
                                const batchNumber = b.batchNumber || `BATCH-${idx + 1}`;
                                const mfg = b.mfgDate ?? b.manufacturingDate ?? null;
                                const exp = b.expiryDate ?? null;
                                const avail = Number(b.availableQuantity ?? b.quantity ?? 0);
                                const batchKey = getReturnBatchStateKey(b, idx);
                                const inputKey = `${pid}:${batchKey}`;
                                const isApplied = Number(batchSelections[pid]?.[batchKey] || 0) > 0;
                                const appliedQty = Number(batchSelections[pid]?.[batchKey] || 0);
                                const value = receiveQtyMap[inputKey] ?? (isApplied ? String(appliedQty) : '');

                                return (
                                  <div
                                    key={b.id || b._id || b.batchId || `${batchNumber}${idx}`}
                                    className="grid grid-cols-6 gap-3 items-center py-2 border-b border-gray-100 text-sm"
                                  >
                                    <span className="text-gray-900">{batchNumber}</span>
                                    <span className="text-gray-700">
                                      {mfg ? new Date(mfg).toLocaleDateString() : '-'}
                                    </span>
                                    <span className="text-gray-700">
                                      {exp ? new Date(exp).toLocaleDateString() : '-'}
                                    </span>
                                    <span className="text-right text-gray-700">{avail}</span>
                                    <div className="flex justify-end items-center gap-2">
                                      {isApplied ? (
                                        <span className="font-medium text-gray-900">{appliedQty}</span>
                                      ) : (
                                        <>
                                          <input
                                            type="number"
                                            min="0"
                                            max={avail}
                                            className="w-20 border rounded px-2 py-1 text-sm"
                                            value={value}
                                            disabled={isFullyReturned}
                                            onChange={(e) => handleBatchQtyInputChange(inputKey, e.target.value)}
                                          />
                                          <span className="text-gray-400 text-xs">/ {avail}</span>
                                        </>
                                      )}
                                    </div>
                                    <div className="flex justify-end">
                                      {isApplied ? (
                                        <button
                                          type="button"
                                          onClick={() => removeBatchAllocation(pid, batchKey, inputKey)}
                                          disabled={isFullyReturned}
                                          className={`text-sm font-medium ${
                                            isFullyReturned ? 'cursor-not-allowed text-red-300' : 'text-red-600 hover:text-red-800'
                                          }`}
                                        >
                                          Remove
                                        </button>
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={() => applyBatchAllocation(pid, batchKey, batchNumber, inputKey, avail)}
                                          disabled={isFullyReturned}
                                          className={`rounded-lg border px-3 py-1 text-sm font-medium ${
                                            isFullyReturned
                                              ? 'cursor-not-allowed border-gray-300 text-gray-400'
                                              : 'border-emerald-500 text-emerald-600 hover:bg-emerald-50'
                                          }`}
                                        >
                                          Apply
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="py-3 text-sm text-gray-500">No batches found for this product.</div>
                            )}
                          </div>

                          <div className="mt-3 flex items-center justify-between text-sm">
                            <span className="text-gray-600">
                              Allocated Total:{' '}
                              <span className="font-semibold text-gray-900">{getProductBatchTotal(pid)}</span>
                            </span>
                            <span className="text-gray-600">
                              Return Quantity:{' '}
                              <span className="font-semibold text-gray-900">
                                {getProductReceivedQtyNumber(pid)}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {invoiceProducts.length === 0 && selectedInvoiceData && (
              <div className="text-sm text-gray-500 text-center py-4">
                <p>No products found for invoice: {selectedInvoiceData.invoiceNumber}</p>
                <p className="text-xs mt-2">Please check if the invoice has items in outward management.</p>
              </div>
            )}
            {selectedProducts.length > 0 && batchAllocationValidationMessage && (
              <div className="mt-3 text-sm text-red-600">
                {batchAllocationValidationMessage}
              </div>
            )}
          </div>
        )}
        {/* OLD batch UI removed */}
      </FormPopup>
      <ReturnDetailsModal
        open={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setViewData(null);
          setViewError(null);
        }}
        loading={viewLoading}
        error={viewError}
        data={viewData}
        onRetry={() => viewRowRef.current && openView(viewRowRef.current)}
      />
      <div className="mb-3 flex items-center justify-end gap-2">
</div>
      {loading ? (
        <SkeletonTable cols={returnColumns.length + 1} rows={8} />
      ) : (
        <Table
          columns={returnColumns}
          data={filteredTransactions}
          onRowClick={(row) => openView(row)}
          loading={false}
          emptyMessage="No return transactions found"
          actions={returnActions}
        />
      )}
    </div>
  );
}
